import { Download, StickyNote } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  answerSet,
  cellFor,
  changed,
  compareCsv,
  csvFilename,
  defaultVisible,
  groupRows,
  isNew,
  latestVersion,
  notesFor,
  rewordedIn,
  summary,
  toggleVersion,
} from "./compare";
import { useMe } from "@/api/hooks/auth";
import { useAdminProject, useResponsesCompare, useSaveResponseNote, type ResponsesCompare } from "@/api/hooks/admin";
import { errorMessage } from "@/api/errors";
import type { CompareQuestion, CompareQuestionVersion } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip } from "@/components/ui/tooltip";
import { answerText } from "@/features/questionnaire/answerText";
import { formatDate } from "@/lib/dates";
import { deviceFromWidth } from "@/lib/device";
import { downloadCsv } from "@/lib/download";
import { cn } from "@/lib/utils";
import { usePageCrumbs } from "@/app/layouts/shellContext";

type NoteTarget = { id: string; title: string; note: string };

function NoteDialog({ projectId, version, target, onClose }: { projectId: string; version: number; target: NoteTarget; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSaveResponseNote(projectId, version);
  const [note, setNote] = useState(target.note);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.responses.note")}</DialogTitle>
          <DialogDescription>{target.title}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(
              { question_id: target.id, note },
              {
                onSuccess: () => {
                  toast.success(t("admin.responses.noteSaved"));
                  onClose();
                },
              },
            );
          }}
        >
          <FormError message={save.isError ? errorMessage(save.error, t("errors.generic")) : null} />
          <Field label={t("admin.responses.note")} hint={t("admin.responses.noteHint")}>
            {(p) => <Textarea {...p} rows={4} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="danger" disabled={save.isPending}>
              {t("admin.responses.saveNote")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RewordedBadge({ version, previous }: { version: number; previous: CompareQuestionVersion }) {
  const { t } = useTranslation();
  const options = previous.options.map((o) => o.label).join(", ");
  return (
    <Tooltip
      content={
        <span className="flex flex-col gap-1">
          <span>{t("admin.responses.earlierWording", { version: previous.version, title: previous.title })}</span>
          {options ? <span>{t("admin.responses.earlierOptions", { options })}</span> : null}
        </span>
      }
    >
      <button type="button" className="rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
        <Badge variant="info">{t("admin.responses.rewordedIn", { version })}</Badge>
      </button>
    </Tooltip>
  );
}

function NotesCell({
  c,
  q,
  meId,
  onEdit,
}: {
  c: ResponsesCompare;
  q: CompareQuestion;
  meId: string | undefined;
  onEdit: (note: string) => void;
}) {
  const { t } = useTranslation();
  const { latest, earlier } = notesFor(c, q.id);
  const mine = latest.find((n) => n.author_user_id === meId);
  return (
    <div className="flex flex-col gap-2 text-sm">
      {latest.map((n) => (
        <p key={n.author_user_id} className="flex flex-col">
          <span className="text-xs text-fg-muted">
            {t("admin.responses.noteBy", { author: n.author_name, date: n.updated_at ? formatDate(n.updated_at) : "" })}
          </span>
          <span className="whitespace-pre-wrap">{n.note}</span>
        </p>
      ))}
      <button
        type="button"
        onClick={() => onEdit(mine?.note ?? "")}
        aria-label={t("admin.responses.noteFor", { title: q.versions[0]!.title })}
        className="flex min-h-11 items-start gap-2 self-start rounded-md px-2 py-1 text-left text-sm text-heading underline hover:bg-secondary-subtle"
      >
        <StickyNote aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        {mine ? t("admin.responses.editNote") : t("admin.responses.addNote")}
      </button>
      {earlier.map((n) => (
        <p key={`${n.version}-${n.author_user_id}`} className="flex flex-col text-fg-muted">
          <span className="text-xs">{t("admin.responses.earlierNote", { version: n.version, author: n.author_name })}</span>
          <span className="whitespace-pre-wrap">{n.note}</span>
        </p>
      ))}
    </div>
  );
}

/** Staff Responses across versions. Inside a project workspace pass `projectId`; under /admin it uses the URL. */
export function ResponsesMatrixPage({ projectId }: { projectId?: string } = {}) {
  const { t } = useTranslation();
  const params = useParams();
  const id = projectId ?? params.id ?? "";
  const project = useAdminProject(id);
  const compare = useResponsesCompare(id);
  const me = useMe();
  usePageCrumbs(
    [
      { label: t("admin.nav.orgs"), to: "/admin" },
      { label: project.data?.name ?? "", to: `/admin/projects/${id}` },
      { label: t("admin.responses.title") },
    ],
    !projectId,
  );
  const [chosen, setChosen] = useState<number[] | null>(null);
  const [noteFor, setNoteFor] = useState<NoteTarget | null>(null);

  if (compare.isPending || project.isPending) return <PageLoading />;
  if (compare.isError) return <ErrorNotice error={compare.error} />;
  if (project.isError) return <ErrorNotice error={project.error} />;
  const c = compare.data;
  const p = project.data;
  const latest = latestVersion(c);
  const visible = chosen ?? defaultVisible(c, typeof window !== "undefined" && deviceFromWidth(window.innerWidth) === "phone");
  const s = summary(c);
  const fileName = (fid: string) => c.file_names[fid];
  const labels = { yes: t("questionnaire.yes"), no: t("questionnaire.no"), fileRemoved: t("questionnaire.answers.fileRemoved") };
  const format = (entry: CompareQuestionVersion, value: Parameters<typeof answerText>[1]) => answerText(entry, value, fileName, labels);
  const words = { notAsked: t("admin.responses.notAsked"), noLongerAsked: t("admin.responses.noLongerAsked") };
  const cols = 2 + visible.length;

  const summaryText = s
    ? [
        s.status === "none"
          ? t("admin.responses.summaryNone", { version: s.version })
          : s.status === "submitted" && s.submittedBy
            ? t("admin.responses.summaryBy", { version: s.version, name: s.submittedBy.name })
            : t("admin.responses.summary", {
                version: s.version,
                status: t(`admin.responses.statusLower.${s.status}`),
                answered: s.answered,
                total: s.total,
              }),
        s.since !== null
          ? t("admin.responses.summarySince", {
              added: t("admin.responses.newQuestions", { count: s.added }),
              reworded: t("admin.responses.rewordedCount", { count: s.reworded }),
              since: s.since,
            })
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${t("admin.responses.title")}: ${p.name}`}
        description={
          <span className="flex flex-col gap-1">
            <span>
              <Link to={`/admin/projects/${p.id}`}>{p.name}</Link> · {t("admin.responses.intro")}
            </span>
            {summaryText ? <span className="font-medium text-heading">{summaryText}</span> : null}
          </span>
        }
        actions={
          latest !== null ? (
            <Button variant="secondary" onClick={() => downloadCsv(csvFilename(p.slug, visible), compareCsv(c, visible, format, words))}>
              <Download aria-hidden="true" className="h-4 w-4" />
              {t("admin.responses.download")}
            </Button>
          ) : null
        }
      />
      {latest === null ? <EmptyState title={t("admin.responses.noTemplate")} /> : null}
      {latest !== null && c.answers.length === 0 ? <EmptyState title={t("admin.responses.empty")} /> : null}
      {latest !== null && c.answers.length > 0 ? (
        <>
          <div className="flex flex-col gap-2">
            <div role="group" aria-label={t("admin.responses.versionsShown")} className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-heading">{t("admin.responses.versionsShown")}</span>
              {c.versions.map((v) => {
                const on = visible.includes(v.version);
                return (
                  <button
                    key={v.version}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setChosen(toggleVersion(visible, v.version))}
                    className={cn(
                      "min-h-11 rounded-pill border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                      on ? "border-secondary bg-secondary text-secondary-fg" : "border-border-strong bg-surface text-fg-muted hover:border-accent",
                    )}
                  >
                    {t("admin.responses.versionChip", { version: v.version })}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="relative w-full overflow-x-auto rounded-lg border border-border bg-surface shadow-card">
            <table className="w-max min-w-full border-collapse text-left sm:w-full sm:min-w-matrix">
              <thead className="bg-surface text-sm">
                <tr className="border-b border-border">
                  <th scope="col" rowSpan={2} className="sticky left-0 z-raised w-40 min-w-40 bg-surface px-3 py-3 align-bottom font-semibold sm:w-auto sm:min-w-56 sm:px-4">
                    {t("admin.responses.question")}
                  </th>
                  <th scope="colgroup" colSpan={visible.length} className="border-l border-border px-4 pt-3 font-semibold">
                    {t("admin.responses.answers")}
                  </th>
                  <th scope="col" rowSpan={2} className="min-w-48 border-l border-border px-4 py-3 align-bottom font-semibold">
                    {t("admin.responses.note")}
                  </th>
                </tr>
                <tr className="border-b border-border">
                  {visible.map((v, i) => {
                    const set = answerSet(c, v);
                    const status = set?.status ?? "none";
                    return (
                      <th key={v} scope="col" className={cn("min-w-36 px-4 pb-3 pt-1 align-top text-xs font-normal", i === 0 && "border-l border-border")}>
                        <span className="flex flex-col gap-0.5">
                          <span className={cn("font-medium", status === "draft" ? "text-warning-fg" : "text-heading")}>
                            {t("admin.responses.subHeader", { version: v, status: t(`admin.responses.status.${status}`) })}
                          </span>
                          {set?.submitted_by ? (
                            <span className="text-fg-muted">{t("admin.responses.submittedBy", { name: set.submitted_by.name })}</span>
                          ) : null}
                          {set?.submitted_at ? <span className="text-fg-muted">{formatDate(set.submitted_at)}</span> : null}
                          {set && v !== latest ? <span className="text-fg-subtle">{t("admin.responses.replacedBy", { version: latest })}</span> : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {groupRows(c).map((group) => (
                <tbody key={group.id ?? "gone"}>
                  <tr className="bg-secondary-subtle">
                    <th scope="rowgroup" colSpan={cols} className="px-4 py-2 font-heading text-sm font-semibold text-heading">
                      {group.title ?? t("admin.responses.noLongerAsked")}
                    </th>
                  </tr>
                  {group.questions.map((q) => {
                    const reworded = rewordedIn(c, q);
                    const title = q.versions[0]!.title;
                    return (
                      <tr key={q.id} className="border-b border-border align-top">
                        <th
                          scope="row"
                          className="sticky left-0 z-raised bg-surface px-3 py-3 text-sm font-normal sm:px-4 sm:text-base"
                        >
                          <span className="flex flex-col gap-1">
                            <span className="font-medium">{title}</span>
                            {isNew(c, q) || reworded ? (
                              <span className="flex flex-wrap gap-1">
                                {isNew(c, q) ? <Badge variant="success">{t("admin.responses.new")}</Badge> : null}
                                {reworded ? <RewordedBadge version={reworded.version} previous={reworded.previous} /> : null}
                              </span>
                            ) : null}
                            <code className="hidden text-xs text-fg-muted sm:block">{q.id}</code>
                          </span>
                        </th>
                        {visible.map((v, i) => {
                          const cell = cellFor(c, q, v);
                          const border = i === 0 && "border-l border-border";
                          if (cell.kind === "notAsked") {
                            return (
                              <td key={v} className={cn("px-4 py-3 text-sm text-fg-subtle", border)}>
                                {t("admin.responses.notAsked")}
                              </td>
                            );
                          }
                          const text = format(cell.entry, cell.value);
                          const older = visible.find((x) => x < v);
                          return (
                            <td key={v} className={cn("whitespace-pre-wrap px-4 py-3", border)}>
                              <span className="flex max-w-48 flex-col items-start gap-1 sm:max-w-none">
                                <span>{text || <span className="text-fg-subtle">—</span>}</span>
                                {cell.by ? <span className="text-xs text-fg-muted">{t("admin.responses.by", { name: cell.by.name })}</span> : null}
                                {changed(c, q, visible, v) ? (
                                  <Badge variant="warning" title={t("admin.responses.changedHint", { version: older })}>
                                    {t("admin.responses.changed")}
                                  </Badge>
                                ) : null}
                              </span>
                            </td>
                          );
                        })}
                        <td className="border-l border-border px-4 py-3">
                          <NotesCell c={c} q={q} meId={me.data?.id} onEdit={(note) => setNoteFor({ id: q.id, title, note })} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        </>
      ) : null}
      {noteFor && latest !== null ? (
        <NoteDialog key={noteFor.id} projectId={id} version={latest} target={noteFor} onClose={() => setNoteFor(null)} />
      ) : null}
    </div>
  );
}
